import { Component, ErrorInfo, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  AlertTriangle, 
  RefreshCw, 
  Home, 
  Bug, 
  Copy,
  Send,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
  bugReport: string;
  isSubmittingReport: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      bugReport: '',
      isSubmittingReport: false
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error
    };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // In a real app, you would send this to your error reporting service
    // Example: Sentry.captureException(error, { extra: errorInfo });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      bugReport: '',
      isSubmittingReport: false
    });
  };

  handleGoHome = () => {};

  handleCopyError = async () => {
    const errorDetails = this.getErrorDetails();
    try {
      await navigator.clipboard.writeText(errorDetails);
      toast.success('Error details copied to clipboard');
    } catch {
      toast.error('Failed to copy error details');
    }
  };

  handleSubmitBugReport = async () => {
    if (!this.state.bugReport.trim()) {
      toast.error('Please describe what you were doing when the error occurred');
      return;
    }

    this.setState({ isSubmittingReport: true });

    try {
      // Simulate API call to submit bug report
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // In a real app, you would send this to your backend
      const reportData = {
        error: this.state.error?.message,
        stack: this.state.error?.stack,
        componentStack: this.state.errorInfo?.componentStack,
        userDescription: this.state.bugReport,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      console.log('Bug report submitted:', reportData);
      
      toast.success('Bug report submitted successfully', {
        description: 'Thank you for helping us improve the application'
      });
      
      this.setState({ bugReport: '' });
    } catch {
      toast.error('Failed to submit bug report', {
        description: 'Please try again or contact support directly'
      });
    } finally {
      this.setState({ isSubmittingReport: false });
    }
  };

  getErrorDetails = () => {
    const { error, errorInfo } = this.state;
    return `Error: ${error?.message || 'Unknown error'}

Stack Trace:
${error?.stack || 'No stack trace available'}

Component Stack:
${errorInfo?.componentStack || 'No component stack available'}

Timestamp: ${new Date().toISOString()}
User Agent: ${navigator.userAgent}
URL: ${window.location.href}`;
  };

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted/20 p-4">
          <div className="max-w-2xl w-full space-y-6">
            {/* Main Error Card */}
            <Card className="border-2 border-destructive/20 bg-gradient-to-br from-background to-destructive/5">
              <CardHeader className="text-center space-y-4">
                <div className="flex justify-center">
                  <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
                    <AlertTriangle className="h-8 w-8 text-destructive" />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <CardTitle className="text-2xl md:text-3xl font-bold tracking-tight">
                    Something went wrong
                  </CardTitle>
                  <CardDescription className="text-base md:text-lg">
                    We're sorry, but an unexpected error occurred. Our team has been notified and is working on a fix.
                  </CardDescription>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-6">
                {/* Primary Actions */}
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button 
                    onClick={this.handleRetry}
                    className="flex items-center space-x-2"
                  >
                    <RefreshCw className="h-4 w-4" />
                    <span>Try Again</span>
                  </Button>
                  
                  <Button asChild variant="outline" className="flex items-center space-x-2">
                    <Link to="/">
                      <Home className="h-4 w-4" />
                      <span>Go Home</span>
                    </Link>
                  </Button>
                </div>

                {/* Error Details Toggle */}
                <div className="space-y-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={this.toggleDetails}
                    className="w-full flex items-center justify-center space-x-2 text-muted-foreground hover:text-foreground"
                  >
                    <Bug className="h-4 w-4" />
                    <span>Technical Details</span>
                    {this.state.showDetails ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>

                  {this.state.showDetails && (
                    <Card className="bg-muted/30 border-dashed">
                      <CardContent className="p-4 space-y-4">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">Error Details</Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={this.handleCopyError}
                              className="flex items-center space-x-1"
                            >
                              <Copy className="h-3 w-3" />
                              <span>Copy</span>
                            </Button>
                          </div>
                          <Textarea
                            value={this.getErrorDetails()}
                            readOnly
                            className="font-mono text-xs min-h-[120px] resize-none"
                          />
                        </div>

                        <Alert>
                          <Bug className="h-4 w-4" />
                          <AlertDescription className="text-xs">
                            <strong>For developers:</strong> This information can help debug the issue. 
                            Please include it when reporting the bug.
                          </AlertDescription>
                        </Alert>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Bug Report Card */}
            <Card className="border-dashed">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2 text-lg">
                  <Bug className="h-5 w-5" />
                  <span>Report this Issue</span>
                </CardTitle>
                <CardDescription>
                  Help us fix this problem by describing what you were doing when it occurred.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bug-report">What were you doing when this error occurred?</Label>
                  <Textarea
                    id="bug-report"
                    placeholder="Please describe the steps you took before seeing this error..."
                    value={this.state.bugReport}
                    onChange={(e) => this.setState({ bugReport: e.target.value })}
                    className="min-h-[100px] resize-none"
                  />
                </div>

                <Button
                  onClick={this.handleSubmitBugReport}
                  disabled={this.state.isSubmittingReport || !this.state.bugReport.trim()}
                  className="w-full flex items-center space-x-2"
                >
                  {this.state.isSubmittingReport ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Submitting Report...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Submit Bug Report</span>
                    </>
                  )}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Your report helps us improve the application for everyone. Thank you!
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
